import React, { useState, useEffect, useRef } from "react";
import Fuse from "fuse.js";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./PodcastFilter.css";
import audioNames from "./Data/AllPodcastData.json";
import AudioPlayer from "./player";
import PodcastCarousel from "./PodcastCarousel";
import RelatedNovelsForPodcast from "./RelatedNovelsForPodcast";
import RelatedDataVisForPodcast from "./RelatedDataVisForPodcast";
import RelatedBriefsForPodcast from "./RelatedBriefsForPodcast";
import RelatedArchivesForPodcast from "./RelatedArchivesForPodcast";

const PodcastFilterBar = ({ onLinkClick }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // Find the initial podcast
  const initialPodcast = audioNames.find(
    (audio) => audio.name === "The 100th Episode of Remarkable Receptions"
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState({});
  const [filteredLinks, setFilteredLinks] = useState(audioNames);
  const [selectedAudio, setSelectedAudio] = useState(initialPodcast);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(true); // State to toggle detail view
  const [isCitationVisible, setIsCitationVisible] = useState(false);
  const [citationStyle, setCitationStyle] = useState("MLA");
  const initialRender = useRef(true); // Ref to track initial render

  useEffect(() => {
    if (id) {
      const podcast = audioNames.find((audio) => audio.id === id);
      if (podcast) {
        setSelectedAudio(podcast);
        setShowDetails(true);
      }
    }
  }, [id]);

  const handleAudioClick = (audio) => {
    navigate(`/podcasts/${audio.id}`);
    setSelectedAudio(audio);
    setShowDetails(true); // Show details when a result is clicked
  };

  const uniqueCategories = Array.from(
    new Set(audioNames.flatMap((audio) => audio.tags || []))
  );

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false; // Mark initial render as completed
      setFilteredLinks(audioNames); // Ensure all audio names are displayed initially
    } else if (searchTerm || Object.keys(selectedCategories).length) {
      const fuse = new Fuse(audioNames, {
        keys: ["name", "categories", "host", "notes"],
        threshold: 0.5, // Adjust the threshold for more or less fuzzy matches
      });

      const searchResults = searchTerm
        ? fuse.search(searchTerm).map(({ item }) => item)
        : audioNames;

      const filtered = searchResults.filter(
        (audio) =>
          Object.keys(selectedCategories).length === 0 ||
          audio.tags.some((category) => selectedCategories[category])
      );

      setFilteredLinks(filtered);
      setShowDetails(false); // Hide details when a search is performed
    }
  }, [searchTerm, selectedCategories]);

  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) => {
      const newState = { ...prev, [category]: !prev[category] };
      const activeCategories = Object.keys(newState).filter(
        (key) => newState[key]
      );

      if (activeCategories.length === 0) {
        return {};
      }

      return newState;
    });
  };

  const TagButton = ({ children, onClick }) => (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        padding: "6px 12px",
        alignItems: "center",
        gap: "4px",
        borderRadius: "8px",
        border: "0.25px solid var(--Gray-Black, #00000090)",
        background: "transparent",
        cursor: "pointer",
        color: "#00010990",
      }}
    >
      {children}
    </button>
  );

  const toggleVisibility = () => {
    if (window.pageYOffset > 500) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", toggleVisibility);
    return () => {
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, []);

  const toggleTranscript = () => {
    setIsExpanded(!isExpanded);
  };

  const onTagClick = (tag) => {
    navigate(`/novels?category=${encodeURIComponent(tag)}`);
  };

  const handleCiteClick = () => {
    setIsCitationVisible(true);
  };

  const handleCloseCitation = () => {
    setIsCitationVisible(false);
  };

  const handleCopyToClipboard = () => {
    const citationText = generateCitationText(citationStyle);
    navigator.clipboard.writeText(citationText).then(
      () => {
        alert("Citation copied to clipboard!");
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  const generateCitationText = (style) => {
    const {
      name: title,
      host: author,
      date,
      season,
      episode_number,
      path,
    } = selectedAudio;
    const url = window.location.origin + path;

    switch (style) {
      case "MLA":
        return `${author}. “${title}.” Remarkable Receptions, season ${season}, episode ${episode_number}, ${date}, ${url}. Accessed ${new Date().toLocaleDateString()}.`;
      case "APA":
        return `${author}. (Host). (${new Date(date).toLocaleDateString(
          "en-US",
          { year: "numeric", month: "long", day: "numeric" }
        )}). ${title} (No. ${episode_number}) [Audio podcast episode]. In Remarkable Receptions. ${url}`;
      case "Chicago":
        return `${author}. “${title}”. Produced by [Producer Name]. Remarkable Receptions, ${new Date(
          date
        ).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}. Podcast, [Length]. ${url}.`;
      default:
        return "";
    }
  };

  const generateCitation = (style) => {
    const citationText = generateCitationText(style);
    const [author, title, ...rest] = citationText.split(/(?<=\.|\?)\s/);

    return (
      <>
        {author} <i>{title}</i> {rest.join(" ")}
      </>
    );
  };

  const audioPath = selectedAudio
    ? `${process.env.PUBLIC_URL}/${selectedAudio.path}`
    : "";
  const [imagePath, setImageSrc] = useState("");

  useEffect(() => {
    if (selectedAudio) {
      const pngPath = `${process.env.PUBLIC_URL}/podcast/images/${selectedAudio.name}.png`;
      const jpgPath = `${process.env.PUBLIC_URL}/podcast/images/${selectedAudio.name}.jpg`;

      // Create an Image object to check if it exists
      const image = new Image();
      image.src = pngPath;

      image.onload = () => {
        // If .png exists, use it
        setImageSrc(pngPath);
      };

      image.onerror = () => {
        // Otherwise, try .jpg
        setImageSrc(jpgPath);
      };
    }
  }, [selectedAudio]);

  return (
    <div className="PodcastFilter">
      {isVisible && (
        <button
          className="back-to-top-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
        >
          <span className="back-to-top-text">Back to Top</span>
        </button>
      )}
      <div className="filter-bar">
        <div className="filter-bar-header">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
          >
            <path
              d="M4.19656 4.19487C3.30269 5.08864 2.59363 6.14974 2.10987 7.31756C1.62611 8.48539 1.37712 9.73706 1.37712 11.0011C1.37712 12.2652 1.62611 13.5168 2.10987 14.6847C2.59363 15.8525 3.30269 16.9136 4.19656 17.8074C4.3218 17.937 4.39109 18.1107 4.38953 18.291C4.38796 18.4712 4.31566 18.6436 4.18819 18.7711C4.06072 18.8986 3.88829 18.9709 3.70802 18.9725C3.52776 18.974 3.3541 18.9047 3.22444 18.7795C2.20293 17.758 1.39263 16.5454 0.83979 15.2108C0.286953 13.8761 0.00241089 12.4457 0.00241089 11.0011C0.00241089 9.55653 0.286953 8.12609 0.83979 6.79147C1.39263 5.45685 2.20293 4.24419 3.22444 3.22274C3.3541 3.09751 3.52776 3.02821 3.70802 3.02978C3.88829 3.03135 4.06072 3.10365 4.18819 3.23112C4.31566 3.35859 4.38796 3.53102 4.38953 3.71128C4.39109 3.89154 4.3218 4.0652 4.19656 4.19487ZM7.11431 7.11262C6.08322 8.14402 5.50399 9.54271 5.50399 11.0011C5.50399 12.4595 6.08322 13.8582 7.11431 14.8896C7.17823 14.9535 7.22894 15.0294 7.26353 15.1129C7.29812 15.1965 7.31593 15.286 7.31593 15.3764C7.31593 15.4668 7.29812 15.5563 7.26353 15.6398C7.22894 15.7233 7.17823 15.7992 7.11431 15.8631C7.05039 15.927 6.97451 15.9777 6.89099 16.0123C6.80747 16.0469 6.71796 16.0647 6.62756 16.0647C6.53716 16.0647 6.44765 16.0469 6.36413 16.0123C6.28062 15.9777 6.20473 15.927 6.14081 15.8631C5.50222 15.2247 4.99565 14.4667 4.65003 13.6325C4.30442 12.7983 4.12653 11.9041 4.12653 11.0011C4.12653 10.0981 4.30442 9.20398 4.65003 8.36975C4.99565 7.53552 5.50222 6.77754 6.14081 6.13912C6.26991 6.01002 6.44499 5.9375 6.62756 5.9375C6.81013 5.9375 6.98522 6.01002 7.11431 6.13912C7.24341 6.26821 7.31593 6.4433 7.31593 6.62587C7.31593 6.80843 7.24341 6.98352 7.11431 7.11262V7.11262ZM14.8913 6.13912C14.9552 6.07509 15.031 6.0243 15.1146 5.98964C15.1981 5.95498 15.2876 5.93714 15.3781 5.93714C15.4685 5.93714 15.558 5.95498 15.6416 5.98964C15.7251 6.0243 15.8009 6.07509 15.8648 6.13912C16.5034 6.77754 17.01 7.53552 17.3556 8.36975C17.7012 9.20398 17.8791 10.0981 17.8791 11.0011C17.8791 11.9041 17.7012 12.7983 17.3556 13.6325C17.01 14.4667 16.5034 15.2247 15.8648 15.8631C15.8009 15.927 15.725 15.9777 15.6415 16.0123C15.558 16.0469 15.4685 16.0647 15.3781 16.0647C15.2877 16.0647 15.1982 16.0469 15.1146 16.0123C15.0311 15.9777 14.9552 15.927 14.8913 15.8631C14.8274 15.7992 14.7767 15.7233 14.7421 15.6398C14.7075 15.5563 14.6897 15.4668 14.6897 15.3764C14.6897 15.286 14.7075 15.1965 14.7421 15.1129C14.7767 15.0294 14.8274 14.9535 14.8913 14.8896C15.9224 13.8582 16.5016 12.4595 16.5016 11.0011C16.5016 9.54271 15.9224 8.14402 14.8913 7.11262C14.8273 7.04875 14.7765 6.97289 14.7418 6.88936C14.7072 6.80584 14.6893 6.7163 14.6893 6.62587C14.6893 6.53544 14.7072 6.4459 14.7418 6.36237C14.7765 6.27885 14.8273 6.20298 14.8913 6.13912ZM17.8091 3.22412C17.938 3.09523 18.1128 3.02283 18.2951 3.02283C18.4774 3.02283 18.6523 3.09523 18.7812 3.22412C20.8438 5.28698 22.0025 8.08466 22.0025 11.0018C22.0025 13.9189 20.8438 16.7166 18.7812 18.7795C18.6515 18.9047 18.4779 18.974 18.2976 18.9725C18.1173 18.9709 17.9449 18.8986 17.8174 18.7711C17.69 18.6436 17.6177 18.4712 17.6161 18.291C17.6145 18.1107 17.6838 17.937 17.8091 17.8074C18.7029 16.9136 19.412 15.8525 19.8958 14.6847C20.3795 13.5168 20.6285 12.2652 20.6285 11.0011C20.6285 9.73706 20.3795 8.48539 19.8958 7.31756C19.412 6.14974 18.7029 5.08864 17.8091 4.19487C17.6802 4.06594 17.6078 3.89111 17.6078 3.70881C17.6078 3.5265 17.6802 3.35167 17.8091 3.22274V3.22412ZM13.7528 11.0011C13.7528 11.7305 13.4631 12.4299 12.9474 12.9457C12.4316 13.4614 11.7322 13.7511 11.0028 13.7511C10.2735 13.7511 9.57399 13.4614 9.05827 12.9457C8.54254 12.4299 8.25281 11.7305 8.25281 11.0011C8.25281 10.2718 8.54254 9.5723 9.05827 9.05657C9.57399 8.54085 10.2735 8.25112 11.0028 8.25112C11.7322 8.25112 12.4316 8.54085 12.9474 9.05657C13.4631 9.5723 13.7528 10.2718 13.7528 11.0011V11.0011Z"
              fill="white"
            />
          </svg>
          Remarkable Receptions Podcasts
        </div>
        <div className="Podcast-Filter-bar">
          <div className="PodcastSearchContainer">
            <input
              className="PodcastSearchBox"
              type="text"
              placeholder="Author, Title, Topic, Keyword"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="search-button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M16.1481 14.2239C17.4795 12.4071 18.0758 10.1545 17.8177 7.91695C17.5597 5.67936 16.4663 3.62171 14.7562 2.15567C13.0462 0.689637 10.8457 -0.0766745 8.59497 0.0100511C6.34422 0.0967767 4.2092 1.03014 2.61707 2.62342C1.02493 4.21669 0.0930909 6.35238 0.0079759 8.60319C-0.0771391 10.854 0.690747 13.054 2.15801 14.7629C3.62527 16.4719 5.6837 17.5638 7.92147 17.8203C10.1592 18.0768 12.4113 17.4788 14.2272 16.1461H14.2258C14.2671 16.2011 14.3111 16.2534 14.3606 16.3042L19.6543 21.598C19.9121 21.856 20.2619 22.001 20.6266 22.0011C20.9914 22.0013 21.3413 21.8565 21.5993 21.5987C21.8573 21.3409 22.0023 20.9911 22.0024 20.6264C22.0025 20.2616 21.8578 19.9117 21.5999 19.6537L16.3062 14.36C16.257 14.3102 16.2042 14.2643 16.1481 14.2225V14.2239ZM16.5028 8.93837C16.5028 9.93149 16.3072 10.9149 15.9272 11.8324C15.5471 12.7499 14.9901 13.5836 14.2878 14.2859C13.5856 14.9881 12.7519 15.5452 11.8344 15.9252C10.9168 16.3053 9.93344 16.5009 8.94032 16.5009C7.9472 16.5009 6.9638 16.3053 6.04628 15.9252C5.12875 15.5452 4.29507 14.9881 3.59283 14.2859C2.89058 13.5836 2.33353 12.7499 1.95348 11.8324C1.57343 10.9149 1.37782 9.93149 1.37782 8.93837C1.37782 6.93267 2.17458 5.00912 3.59283 3.59088C5.01107 2.17263 6.93462 1.37587 8.94032 1.37587C10.946 1.37587 12.8696 2.17263 14.2878 3.59088C15.7061 5.00912 16.5028 6.93267 16.5028 8.93837V8.93837Z"
                  fill="#765623"
                />
              </svg>
            </button>
          </div>
          <div className="PodcastFilters">
            {uniqueCategories.map((category) => (
              <div className="Podcast-select" key={category}>
                <label>
                  <input
                    type="checkbox"
                    checked={!!selectedCategories[category]}
                    onChange={() => handleCategoryChange(category)}
                  />{" "}
                  {category} {}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showDetails ? (
        <div className="Podcast-render">
          <div className="information">
            <div className="pod-info-left">
              <div className="image-wrapper">
                <img
                  src={imagePath}
                  width={250}
                  height={240}
                  alt={`Cover of ${selectedAudio.name}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `${process.env.PUBLIC_URL}/placeholderPic.png`;
                  }}
                />
                <span className="length-badge">{selectedAudio.length}</span>{" "}
                {/* Podcast length */}
              </div>

              <div className="actions">
                <TagButton onClick={handleCiteClick}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M15 15C15.3315 15 15.6494 14.8683 15.8839 14.6339C16.1183 14.3995 16.25 14.0815 16.25 13.75V10.6975C16.25 10.366 16.1183 10.048 15.8839 9.81362C15.6494 9.5792 15.3315 9.4475 15 9.4475H13.265C13.265 9.00875 13.2912 8.56875 13.3425 8.13C13.42 7.665 13.55 7.25125 13.73 6.89C13.9112 6.5275 14.1437 6.24375 14.4287 6.03625C14.7125 5.80375 15.0737 5.6875 15.5137 5.6875V3.75C14.79 3.75 14.1575 3.905 13.6137 4.215C13.0747 4.52186 12.6113 4.94556 12.2575 5.455C11.9024 6.01664 11.6407 6.63212 11.4825 7.2775C11.3231 7.9897 11.2451 8.7177 11.25 9.4475V13.75C11.25 14.0815 11.3817 14.3995 11.6161 14.6339C11.8505 14.8683 12.1685 15 12.5 15H15ZM7.49997 15C7.83149 15 8.14944 14.8683 8.38386 14.6339C8.61828 14.3995 8.74997 14.0815 8.74997 13.75V10.6975C8.74997 10.366 8.61828 10.048 8.38386 9.81362C8.14944 9.5792 7.83149 9.4475 7.49997 9.4475H5.76497C5.76497 9.00875 5.79122 8.56875 5.84247 8.13C5.91997 7.665 6.04997 7.25125 6.22997 6.89C6.41122 6.5275 6.64372 6.24375 6.92872 6.03625C7.21247 5.80375 7.57372 5.6875 8.01372 5.6875V3.75C7.28997 3.75 6.65747 3.905 6.11372 4.215C5.57471 4.52186 5.11128 4.94556 4.75747 5.455C4.40238 6.01664 4.14065 6.63212 3.98247 7.2775C3.82308 7.9897 3.74508 8.7177 3.74997 9.4475V13.75C3.74997 14.0815 3.88167 14.3995 4.11609 14.6339C4.35051 14.8683 4.66845 15 4.99997 15H7.49997Z"
                      fill="#765623"
                    />
                  </svg>
                </TagButton>
                <TagButton>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_2090_572)">
                      <path
                        d="M11 2.50007C10.9999 1.9135 11.2061 1.34559 11.5825 0.895681C11.9588 0.445776 12.4814 0.142533 13.0588 0.039007C13.6361 -0.0645194 14.2315 0.0382633 14.7408 0.329372C15.25 0.620481 15.6406 1.08138 15.8444 1.63143C16.0481 2.18148 16.0519 2.78565 15.8552 3.33824C15.6585 3.89084 15.2737 4.35666 14.7682 4.65421C14.2627 4.95177 13.6687 5.06211 13.0901 4.96592C12.5115 4.86974 11.9851 4.57316 11.603 4.12807L4.88502 7.24807C5.03946 7.73751 5.03946 8.26263 4.88502 8.75207L11.603 11.8721C12.0069 11.4024 12.5707 11.0994 13.1852 11.0217C13.7997 10.9441 14.4212 11.0973 14.9292 11.4518C15.4372 11.8062 15.7955 12.3366 15.9347 12.9402C16.0739 13.5438 15.9841 14.1775 15.6826 14.7186C15.3812 15.2598 14.8896 15.6697 14.3031 15.869C13.7166 16.0683 13.077 16.0429 12.5083 15.7975C11.9396 15.5521 11.4822 15.1043 11.2247 14.5409C10.9673 13.9775 10.9282 13.3386 11.115 12.7481L4.39702 9.62807C4.06446 10.0156 3.62117 10.2921 3.1268 10.4202C2.63243 10.5482 2.11069 10.5219 1.63176 10.3445C1.15283 10.1672 0.739697 9.8475 0.447928 9.42836C0.15616 9.00921 -0.000244141 8.51077 -0.000244141 8.00007C-0.000244141 7.48937 0.15616 6.99093 0.447928 6.57178C0.739697 6.15264 1.15283 5.8329 1.63176 5.65559C2.11069 5.47828 2.63243 5.45189 3.1268 5.57998C3.62117 5.70808 4.06446 5.9845 4.39702 6.37207L11.115 3.25207C11.0386 3.00874 10.9998 2.75514 11 2.50007Z"
                        fill="#765623"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2090_572">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </TagButton>
                <TagButton>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <mask
                      id="mask0_1431_35317"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="20"
                      height="20"
                    >
                      <rect width="20" height="20" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_1431_35317)">
                      <path
                        d="M4.16675 13.3334V9.16675C4.16675 8.93064 4.24661 8.73272 4.40633 8.573C4.56605 8.41328 4.76397 8.33342 5.00008 8.33342C5.23619 8.33342 5.43411 8.41328 5.59383 8.573C5.75355 8.73272 5.83341 8.93064 5.83341 9.16675V13.3334C5.83341 13.5695 5.75355 13.7674 5.59383 13.9272C5.43411 14.0869 5.23619 14.1667 5.00008 14.1667C4.76397 14.1667 4.56605 14.0869 4.40633 13.9272C4.24661 13.7674 4.16675 13.5695 4.16675 13.3334ZM9.16675 13.3334V9.16675C9.16675 8.93064 9.24661 8.73272 9.40633 8.573C9.56605 8.41328 9.76397 8.33342 10.0001 8.33342C10.2362 8.33342 10.4341 8.41328 10.5938 8.573C10.7536 8.73272 10.8334 8.93064 10.8334 9.16675V13.3334C10.8334 13.5695 10.7536 13.7674 10.5938 13.9272C10.4341 14.0869 10.2362 14.1667 10.0001 14.1667C9.76397 14.1667 9.56605 14.0869 9.40633 13.9272C9.24661 13.7674 9.16675 13.5695 9.16675 13.3334ZM2.50008 17.5001C2.26397 17.5001 2.06605 17.4202 1.90633 17.2605C1.74661 17.1008 1.66675 16.9029 1.66675 16.6667C1.66675 16.4306 1.74661 16.2327 1.90633 16.073C2.06605 15.9133 2.26397 15.8334 2.50008 15.8334H17.5001C17.7362 15.8334 17.9341 15.9133 18.0938 16.073C18.2536 16.2327 18.3334 16.4306 18.3334 16.6667C18.3334 16.9029 18.2536 17.1008 18.0938 17.2605C17.9341 17.4202 17.7362 17.5001 17.5001 17.5001H2.50008ZM14.1667 13.3334V9.16675C14.1667 8.93064 14.2466 8.73272 14.4063 8.573C14.5661 8.41328 14.764 8.33342 15.0001 8.33342C15.2362 8.33342 15.4341 8.41328 15.5938 8.573C15.7536 8.73272 15.8334 8.93064 15.8334 9.16675V13.3334C15.8334 13.5695 15.7536 13.7674 15.5938 13.9272C15.4341 14.0869 15.2362 14.1667 15.0001 14.1667C14.764 14.1667 14.5661 14.0869 14.4063 13.9272C14.2466 13.7674 14.1667 13.5695 14.1667 13.3334ZM17.5001 6.66675H2.41675C2.20841 6.66675 2.03133 6.59383 1.8855 6.448C1.73966 6.30217 1.66675 6.12508 1.66675 5.91675V5.45842C1.66675 5.30564 1.70494 5.17369 1.78133 5.06258C1.85772 4.95147 1.95841 4.86119 2.08341 4.79175L9.25008 1.20841C9.48619 1.0973 9.73619 1.04175 10.0001 1.04175C10.264 1.04175 10.514 1.0973 10.7501 1.20841L17.8751 4.77091C18.0279 4.84036 18.1424 4.94453 18.2188 5.08342C18.2952 5.2223 18.3334 5.36814 18.3334 5.52092V5.83342C18.3334 6.06953 18.2536 6.26744 18.0938 6.42717C17.9341 6.58689 17.7362 6.66675 17.5001 6.66675Z"
                        fill="#765623"
                      />
                    </g>
                  </svg>
                </TagButton>
              </div>
            </div>
            <div className="pod-info-right">
              <p className="pod-title">{selectedAudio.name}</p>
              <div className="pod-info-right-meta">
                <p>{selectedAudio.date}</p>
                <p>{selectedAudio.host}</p>
                <p>{selectedAudio.season}</p>
                <p>{selectedAudio.episode_number}</p>
              </div>
              <div className="topic-button-container">
                {selectedAudio.tags &&
                  selectedAudio.tags.map((category, index) => (
                    <TagButton key={index}>{category}</TagButton>
                  ))}
              </div>
              <p className="pod-disc">{selectedAudio.notes}</p>
            </div>
          </div>
          <div className="player-wrapper">
            <AudioPlayer audiofile={{ ...selectedAudio, path: audioPath }} />
          </div>
          <div className="transcript">
            <p>TRANSCRIPT</p>
            <div>
              <p
                className="tran-text"
                dangerouslySetInnerHTML={{
                  __html: isExpanded
                    ? selectedAudio.transcript
                    : `${selectedAudio.transcript.substring(0, 400)}...`,
                }}
              ></p>
            </div>
            <div className="tran-button-wrap">
              <button onClick={toggleTranscript}>
                {isExpanded ? "Show Less" : "View Full Transcript"}
              </button>
            </div>
          </div>

          <div className="pod-caro-wrapper">
            <PodcastCarousel
              selectedPodcast={selectedAudio}
              onPodcastClick={handleAudioClick}
            />
          </div>
          <div className="related-sections">
            <RelatedNovelsForPodcast podcast={selectedAudio} />
            <RelatedDataVisForPodcast podcast={selectedAudio} />
            <RelatedBriefsForPodcast podcast={selectedAudio} />
            <RelatedArchivesForPodcast podcast={selectedAudio} />
          </div>
        </div>
      ) : (
        <div className="searched-pod">
          <p className="searched-pod-heading">Search Results</p>
          <div className="filtered-podcasts">
            {filteredLinks.map((audio, index) => (
              <div
                key={index}
                className="filtered-podcast-item"
                onClick={() => handleAudioClick(audio)}
              >
                <div className="image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/podcast/images/${audio.name}.png`}
                    onError={(e) => {
                      // Attempt to load the .jpg version if .png fails
                      e.target.onerror = () => {
                        // If .jpg fails too, fallback to the placeholder
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/placeholderPic.png`;
                      };
                      e.target.src = `${process.env.PUBLIC_URL}/podcast/images/${audio.name}.jpg`;
                    }}
                    width={170}
                    height={170}
                    alt={`Cover of ${audio.name}`}
                  />
                  <span className="length-badge">{audio.length}</span>{" "}
                  {/* Podcast length */}
                </div>
                <div className="filtered-podcast-info">
                  <p className="filtered-podcast-title">
                    <i>{audio.name}</i>
                  </p>
                  {/* <p className="filtered-podcast-date">{audio.date}</p> */}
                  <p className="filtered-podcast-host">{audio.host}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isCitationVisible && (
        <div className="citation-modal">
          <div className="citation-content">
            <button
              onClick={handleCloseCitation}
              className="close-citation-button"
            >
              X
            </button>
            <div className="citation-styles">
              <button
                onClick={() => setCitationStyle("MLA")}
                className={citationStyle === "MLA" ? "active" : ""}
              >
                MLA
              </button>
              <button
                onClick={() => setCitationStyle("APA")}
                className={citationStyle === "APA" ? "active" : ""}
              >
                APA
              </button>
              <button
                onClick={() => setCitationStyle("Chicago")}
                className={citationStyle === "Chicago" ? "active" : ""}
              >
                Chicago
              </button>
            </div>
            <div className="citation-right">
              <div className="citation-text">
                <p>{generateCitation(citationStyle)}</p>
              </div>
              <button
                onClick={handleCopyToClipboard}
                className="copy-citation-button"
              >
                Copy to Clipboard
              </button>
              <p>
                <b>
                  Remember to check citations for accuracy before including them
                  in your work!
                </b>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastFilterBar;
